import { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  MessageCircle,
  Send,
  Search,
  Phone,
  MoreVertical,
  Check,
  CheckCheck,
  Clock,
  Plus,
  ArrowLeft,
  Car,
  Loader2,
  AlertCircle,
  Bot,
  User
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const WS_URL = BACKEND_URL?.replace('http', 'ws') || 'ws://localhost:8001';

const Mensajes = () => {
  const [conversaciones, setConversaciones] = useState([]);
  const [mensajes, setMensajes] = useState([]);
  const [selectedConversacion, setSelectedConversacion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [nuevoMensaje, setNuevoMensaje] = useState('');
  const [showNewChat, setShowNewChat] = useState(false);
  const [newChatData, setNewChatData] = useState({ telefono: '', nombreContacto: '', mensaje: '' });
  const [isMobileView, setIsMobileView] = useState(false);
  const [showChatOnMobile, setShowChatOnMobile] = useState(false);
  const [llmEnabled, setLlmEnabled] = useState(true);
  const [llmLoading, setLlmLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const selectedConversacionRef = useRef(null);

  // Keep ref in sync with state for WebSocket closure access
  useEffect(() => {
    selectedConversacionRef.current = selectedConversacion;
  }, [selectedConversacion]);

  // WebSocket connection for real-time updates
  useEffect(() => {
    const connectWebSocket = () => {
      // Clean up existing connection
      if (wsRef.current) {
        wsRef.current.close();
      }

      const ws = new WebSocket(`${WS_URL}/api/whatsapp/ws`);

      ws.onopen = () => {
        console.log('WebSocket conectado');
        // Send ping every 30 seconds to keep alive
        const pingInterval = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send('ping');
          }
        }, 30000);
        ws.pingInterval = pingInterval;
      };

      ws.onmessage = (event) => {
        if (event.data === 'pong') return;

        try {
          const data = JSON.parse(event.data);

          if (data.type === 'new_message') {
            // New incoming message
            const newMsg = data.mensaje;
            const currentConv = selectedConversacionRef.current;

            // Only add to messages if viewing this conversation
            if (currentConv && currentConv.id === data.conversation_id) {
              setMensajes(prev => {
                // Check if already exists
                if (prev.some(m => m.id === newMsg.id)) return prev;
                return [...prev, newMsg];
              });
            }

            // Update conversation list
            setConversaciones(prev => {
              const existing = prev.find(c => c.id === data.conversation_id);
              if (existing) {
                const isViewingThis = currentConv && currentConv.id === data.conversation_id;
                return prev.map(c =>
                  c.id === data.conversation_id
                    ? {
                      ...c,
                      ultimoMensaje: newMsg.mensaje,
                      ultimaFecha: newMsg.createdAt,
                      // Only increment unread if not viewing this conversation
                      mensajesNoLeidos: isViewingThis ? 0 : (c.mensajesNoLeidos || 0) + 1
                    }
                    : c
                );
              } else {
                // New conversation - reload list
                loadConversaciones();
                return prev;
              }
            });
          } else if (data.type === 'status_update') {
            // Update message status
            setMensajes(prev => prev.map(m =>
              m.id === data.message_id ? { ...m, estado: data.status } : m
            ));
          }
        } catch (e) {
          console.error('Error parsing WebSocket message:', e);
        }
      };

      ws.onclose = () => {
        console.log('WebSocket desconectado');
        if (ws.pingInterval) clearInterval(ws.pingInterval);
        // Reconnect after 3 seconds
        reconnectTimeoutRef.current = setTimeout(connectWebSocket, 3000);
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
      };

      wsRef.current = ws;
    };

    connectWebSocket();

    return () => {
      if (wsRef.current) {
        if (wsRef.current.pingInterval) clearInterval(wsRef.current.pingInterval);
        wsRef.current.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    loadConversaciones();
    // Check for mobile view
    const checkMobile = () => setIsMobileView(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (selectedConversacion) {
      loadMensajes(selectedConversacion.id);
      loadLlmSettings(selectedConversacion.telefono);
    }
  }, [selectedConversacion]);

  useEffect(() => {
    scrollToBottom();
  }, [mensajes]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadConversaciones = async () => {
    try {
      const response = await axios.get(`${API}/conversaciones`);
      setConversaciones(response.data);
    } catch (error) {
      console.error('Error loading conversaciones:', error);
      toast.error('Error al cargar conversaciones');
    } finally {
      setLoading(false);
    }
  };

  const loadMensajes = async (conversacionId) => {
    try {
      const response = await axios.get(`${API}/conversaciones/${conversacionId}/mensajes`);
      setMensajes(response.data);
      // Update unread count locally
      setConversaciones(prev => prev.map(c =>
        c.id === conversacionId ? { ...c, mensajesNoLeidos: 0 } : c
      ));
    } catch (error) {
      console.error('Error loading mensajes:', error);
      toast.error('Error al cargar mensajes');
    }
  };

  const loadLlmSettings = async (telefono) => {
    try {
      const response = await axios.get(`${API}/conversaciones/${telefono}/llm-settings`);
      setLlmEnabled(response.data.llm_enabled !== false);
    } catch (error) {
      console.error('Error loading LLM settings:', error);
      setLlmEnabled(true); // Default to enabled
    }
  };

  const toggleLlmEnabled = async () => {
    if (!selectedConversacion) return;

    setLlmLoading(true);
    const newValue = !llmEnabled;

    try {
      await axios.put(`${API}/conversaciones/${selectedConversacion.telefono}/llm-settings`, {
        llm_enabled: newValue
      });
      setLlmEnabled(newValue);
      toast.success(newValue
        ? 'Respuesta automática activada'
        : 'Respuesta automática desactivada - puedes responder manualmente'
      );
    } catch (error) {
      console.error('Error updating LLM settings:', error);
      toast.error('Error al actualizar configuración');
    } finally {
      setLlmLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!nuevoMensaje.trim() || !selectedConversacion) return;

    const tempId = `temp-${Date.now()}`;
    const mensajeTexto = nuevoMensaje;

    // Optimistic update: add message with 'enviando' status immediately
    const mensajeOptimista = {
      id: tempId,
      conversacionId: selectedConversacion.id,
      telefono: selectedConversacion.telefono,
      nombreContacto: selectedConversacion.nombreContacto,
      mensaje: mensajeTexto,
      direccion: 'saliente',
      estado: 'enviando',
      createdAt: new Date().toISOString()
    };

    setMensajes(prev => [...prev, mensajeOptimista]);
    setNuevoMensaje('');

    try {
      const response = await axios.post(`${API}/mensajes`, {
        telefono: selectedConversacion.telefono,
        nombreContacto: selectedConversacion.nombreContacto,
        mensaje: mensajeTexto,
        direccion: 'saliente',
        vehiculoId: selectedConversacion.vehiculoId,
        revendedorId: selectedConversacion.revendedorId
      });

      // Replace optimistic message with real one from server
      setMensajes(prev => prev.map(m =>
        m.id === tempId ? response.data : m
      ));

      // Update conversation preview
      setConversaciones(prev => prev.map(c =>
        c.id === selectedConversacion.id
          ? { ...c, ultimoMensaje: mensajeTexto, ultimaFecha: new Date().toISOString() }
          : c
      ));
    } catch (error) {
      console.error('Error sending message:', error);
      // Update message to show error state
      setMensajes(prev => prev.map(m =>
        m.id === tempId ? { ...m, estado: 'fallido' } : m
      ));
      toast.error('Error al enviar mensaje');
    }
  };

  const handleNewChat = async () => {
    if (!newChatData.telefono || !newChatData.nombreContacto || !newChatData.mensaje) {
      toast.error('Completa todos los campos');
      return;
    }

    try {
      const response = await axios.post(`${API}/mensajes`, {
        telefono: newChatData.telefono,
        nombreContacto: newChatData.nombreContacto,
        mensaje: newChatData.mensaje,
        direccion: 'saliente'
      });

      toast.success('Conversación iniciada');
      setShowNewChat(false);
      setNewChatData({ telefono: '', nombreContacto: '', mensaje: '' });
      loadConversaciones();

      // Select the new conversation
      const telefonoNorm = newChatData.telefono.replace(/\s|-/g, '');
      setSelectedConversacion({
        id: `conv-${telefonoNorm}`,
        telefono: newChatData.telefono,
        nombreContacto: newChatData.nombreContacto
      });
      if (isMobileView) setShowChatOnMobile(true);
    } catch (error) {
      console.error('Error creating chat:', error);
      toast.error('Error al iniciar conversación');
    }
  };

  const getStatusIcon = (estado) => {
    switch (estado) {
      case 'enviando':
        return <Loader2 className="h-3 w-3 animate-spin" />;
      case 'enviado':
        return <Check className="h-3 w-3" />;
      case 'entregado':
        return <CheckCheck className="h-3 w-3" />;
      case 'leido':
        return <CheckCheck className="h-3 w-3 text-blue-400" />;
      case 'fallido':
        return <AlertCircle className="h-3 w-3 text-red-400" />;
      case 'pendiente':
        return <Clock className="h-3 w-3" />;
      default:
        return null;
    }
  };

  const formatMessageTime = (dateString) => {
    const date = new Date(dateString);
    return format(date, 'HH:mm', { locale: es });
  };

  const formatConversationDate = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return format(date, 'HH:mm', { locale: es });
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Ayer';
    } else {
      return format(date, 'dd/MM/yy', { locale: es });
    }
  };

  const getInitials = (name) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const filteredConversaciones = conversaciones.filter(c =>
    c.nombreContacto.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.telefono.includes(searchTerm)
  );

  const totalNoLeidos = conversaciones.reduce((acc, c) => acc + (c.mensajesNoLeidos || 0), 0);

  // Render chat list JSX
  const renderChatList = () => (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="p-4 border-b bg-card">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <MessageCircle className="h-6 w-6 text-primary" />
            <h1 className="text-xl font-semibold">Mensajes</h1>
            {totalNoLeidos > 0 && (
              <Badge variant="destructive" className="rounded-full">
                {totalNoLeidos}
              </Badge>
            )}
          </div>
          <Button size="sm" onClick={() => setShowNewChat(true)} data-testid="new-chat-button">
            <Plus className="h-4 w-4 mr-1" />
            Nuevo
          </Button>
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar conversación..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Conversations list */}
      <ScrollArea className="flex-1">
        {filteredConversaciones.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground">
            <MessageCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No hay conversaciones</p>
            <Button className="mt-4" variant="outline" onClick={() => setShowNewChat(true)}>
              Iniciar conversación
            </Button>
          </div>
        ) : (
          filteredConversaciones.map((conv) => (
            <div
              key={conv.id}
              onClick={() => {
                setSelectedConversacion(conv);
                if (isMobileView) setShowChatOnMobile(true);
              }}
              className={`flex items-center gap-3 p-4 cursor-pointer hover:bg-accent/50 transition-colors border-b ${selectedConversacion?.id === conv.id ? 'bg-accent' : ''
                }`}
              data-testid="conversation-item"
            >
              <Avatar className="h-12 w-12">
                <AvatarFallback className="bg-primary/10 text-primary">
                  {getInitials(conv.nombreContacto)}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <span className="font-medium truncate">{conv.nombreContacto}</span>
                  <span className="text-xs text-muted-foreground">
                    {formatConversationDate(conv.ultimaFecha)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <p className="text-sm text-muted-foreground truncate">{conv.ultimoMensaje}</p>
                  {conv.mensajesNoLeidos > 0 && (
                    <Badge variant="default" className="rounded-full ml-2 h-5 min-w-[20px] flex items-center justify-center">
                      {conv.mensajesNoLeidos}
                    </Badge>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </ScrollArea>
    </div>
  );

  // Render chat view JSX
  const renderChatView = () => (
    <div className="flex flex-col h-full">
      {selectedConversacion ? (
        <>
          {/* Chat header */}
          <div className="p-4 border-b bg-card flex items-center gap-3">
            {isMobileView && (
              <Button variant="ghost" size="sm" onClick={() => setShowChatOnMobile(false)}>
                <ArrowLeft className="h-5 w-5" />
              </Button>
            )}
            <Avatar className="h-10 w-10">
              <AvatarFallback className="bg-primary/10 text-primary">
                {getInitials(selectedConversacion.nombreContacto)}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <h2 className="font-semibold">{selectedConversacion.nombreContacto}</h2>
              <p className="text-sm text-muted-foreground flex items-center gap-1">
                <Phone className="h-3 w-3" />
                {selectedConversacion.telefono}
              </p>
            </div>
            {selectedConversacion.vehiculoId && (
              <Badge variant="outline" className="gap-1">
                <Car className="h-3 w-3" />
                Vehículo
              </Badge>
            )}

            {/* LLM Toggle */}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center gap-2 ml-2">
                    <Switch
                      id="llm-toggle"
                      checked={llmEnabled}
                      onCheckedChange={toggleLlmEnabled}
                      disabled={llmLoading}
                      data-testid="llm-toggle"
                    />
                    {llmEnabled ? (
                      <Bot className="h-4 w-4 text-green-500" />
                    ) : (
                      <User className="h-4 w-4 text-blue-500" />
                    )}
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{llmEnabled ? 'Respuesta automática (IA)' : 'Respuesta manual'}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>

          {/* Messages area */}
          <ScrollArea className="flex-1 p-4">
            <div className="space-y-3">
              {mensajes.map((mensaje) => (
                <div
                  key={mensaje.id}
                  className={`flex ${mensaje.direccion === 'saliente' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[75%] rounded-2xl px-4 py-2 ${mensaje.direccion === 'saliente'
                      ? 'bg-primary text-primary-foreground rounded-br-md'
                      : 'bg-muted rounded-bl-md'
                      }`}
                  >
                    <p className="text-sm whitespace-pre-wrap break-words">{mensaje.mensaje}</p>
                    <div className={`flex items-center justify-end gap-1 mt-1 ${mensaje.direccion === 'saliente' ? 'text-primary-foreground/70' : 'text-muted-foreground'
                      }`}>
                      <span className="text-xs">{formatMessageTime(mensaje.createdAt)}</span>
                      {mensaje.direccion === 'saliente' && getStatusIcon(mensaje.estado)}
                    </div>
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>
          </ScrollArea>

          {/* Message input */}
          <div className="p-4 border-t bg-card">
            {llmEnabled ? (
              /* Auto-response mode - disable manual input */
              <div className="flex items-center justify-center gap-2 py-2 text-muted-foreground">
                <Bot className="h-5 w-5 text-green-500" />
                <span className="text-sm">Respuesta automática activada - El asistente responde por ti</span>
              </div>
            ) : (
              /* Manual response mode */
              <div className="flex gap-2">
                <Input
                  placeholder="Escribe un mensaje..."
                  value={nuevoMensaje}
                  onChange={(e) => setNuevoMensaje(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                  className="flex-1"
                  data-testid="message-input"
                />
                <Button onClick={handleSendMessage} disabled={!nuevoMensaje.trim()} data-testid="send-message-button">
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
        </>
      ) : (
        <div className="flex-1 flex items-center justify-center text-muted-foreground">
          <div className="text-center">
            <MessageCircle className="h-16 w-16 mx-auto mb-4 opacity-30" />
            <p className="text-lg">Selecciona una conversación</p>
            <p className="text-sm">o inicia una nueva</p>
          </div>
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-muted-foreground">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-120px)] bg-background">
      <Card className="h-full overflow-hidden">
        <div className="flex h-full">
          {/* Desktop: Show both panels */}
          {!isMobileView && (
            <>
              <div className="w-[350px] border-r h-full">
                {renderChatList()}
              </div>
              <div className="flex-1 h-full">
                {renderChatView()}
              </div>
            </>
          )}

          {/* Mobile: Show one panel at a time */}
          {isMobileView && (
            showChatOnMobile ? (
              <div className="w-full h-full">
                {renderChatView()}
              </div>
            ) : (
              <div className="w-full h-full">
                {renderChatList()}
              </div>
            )
          )}
        </div>
      </Card>

      {/* New chat dialog */}
      <Dialog open={showNewChat} onOpenChange={setShowNewChat}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nueva Conversación</DialogTitle>
            <DialogDescription>
              Inicia una conversación por WhatsApp
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="telefono">Teléfono (WhatsApp) *</Label>
              <Input
                id="telefono"
                placeholder="+598 99 123 456"
                value={newChatData.telefono}
                onChange={(e) => setNewChatData({ ...newChatData, telefono: e.target.value })}
                data-testid="new-chat-phone"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="nombre">Nombre del contacto *</Label>
              <Input
                id="nombre"
                placeholder="Juan Pérez"
                value={newChatData.nombreContacto}
                onChange={(e) => setNewChatData({ ...newChatData, nombreContacto: e.target.value })}
                data-testid="new-chat-name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="mensaje">Mensaje inicial *</Label>
              <Input
                id="mensaje"
                placeholder="Hola, me interesa..."
                value={newChatData.mensaje}
                onChange={(e) => setNewChatData({ ...newChatData, mensaje: e.target.value })}
                data-testid="new-chat-message"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewChat(false)}>
              Cancelar
            </Button>
            <Button onClick={handleNewChat} data-testid="start-chat-button">
              <Send className="h-4 w-4 mr-2" />
              Iniciar Chat
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Mensajes;
